/**
 * Cyannect — Privacy Policy (public page)
 *
 * DRAFT · legal review required. Cyannect-tailored template covering:
 * cy_ref attribution, cashback ledger, Stripe billing, Resend email,
 * Mongo storage, EAS Push, and Canadian PIPEDA/Quebec Law 25 posture.
 */
import Seo from "@/components/Seo";
import { AlertTriangle } from "lucide-react";

const LAST_UPDATED = "February 2026";

export default function Privacy() {
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-white">
      <Seo
        title="Privacy Policy"
        description="Cyannect Privacy Policy — how we collect, use and safeguard personal information for shoppers and merchants across Canada."
        path="/privacy"
      />
      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="privacy-page">
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 mb-8" data-testid="privacy-draft-banner">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong>Draft — legal review required.</strong> This template
            reflects the Cyannect product’s actual data flows (cy_ref,
            cashback, Stripe, Resend, MongoDB, EAS Push) so Canadian counsel
            can validate it against PIPEDA + Quebec Law 25 before launch.
          </div>
        </div>

        <h1 className="font-display text-4xl font-medium text-slate-900">Privacy Policy</h1>
        <p className="text-sm text-slate-500 mt-1">Last updated: {LAST_UPDATED}</p>

        <div className="prose prose-slate max-w-none mt-8 space-y-6 text-slate-700 leading-relaxed">
          <p>
            This Privacy Policy explains how <strong>Cyannect Inc.</strong>
            (“<strong>Cyannect</strong>”, “<strong>we</strong>”, “<strong>us</strong>”)
            collects, uses, discloses and protects personal information when
            you use cyannect.com, app.cyannect.com, our mobile apps or any
            related services (the “<strong>Services</strong>”). Cyannect is
            based in Canada and complies with the Personal Information
            Protection and Electronic Documents Act (PIPEDA) and, where
            applicable, Quebec’s Law 25.
          </p>

          <h2 id="scope">1. Scope</h2>
          <p>
            This Policy covers information about Shoppers (individuals who
            discover and redeem coupons) and Merchant contacts (business
            representatives who administer merchant accounts). It does not
            apply to Merchants’ own customer transactions on their own
            websites — those are governed by each Merchant’s own privacy
            policy.
          </p>

          <h2 id="what">2. Information we collect</h2>
          <h3 className="font-semibold text-slate-900 mt-4">a. Information you provide</h3>
          <ul className="list-disc list-outside pl-5 space-y-1">
            <li>Account details: name, email address, password hash (bcrypt), phone number (optional).</li>
            <li>Merchant profile: business name, category, website, logo, banner, storefront descriptions, province coverage, tax/business identifiers where required.</li>
            <li>Payment details: managed by Stripe. We store your Stripe customer ID and subscription IDs but never receive full card numbers.</li>
            <li>Support correspondence sent to us.</li>
          </ul>
          <h3 className="font-semibold text-slate-900 mt-4">b. Information collected automatically</h3>
          <ul className="list-disc list-outside pl-5 space-y-1">
            <li>Device and log data: IP address, user-agent, timestamps, referrer, campaign/UTM parameters, page paths.</li>
            <li>Attribution: our <strong>cy_ref</strong> token issued when a Shopper activates an offer. It ties an outbound click on the Merchant’s website back to your Cyannect account so we can compute cashback.</li>
            <li>Approximate location: derived server-side from IP address at the province level (province-aware coupon delivery is a core product feature).</li>
            <li>Cookies and local storage: essential session cookies, CSRF tokens, marketing/attribution parameters.</li>
            <li>Push tokens (mobile app only, when you opt in).</li>
          </ul>
          <h3 className="font-semibold text-slate-900 mt-4">c. Information from third parties</h3>
          <ul className="list-disc list-outside pl-5 space-y-1">
            <li>Merchants may report verified sales through the conversions API. The report includes the cy_ref token, order ID, revenue, discount, and product-level line items.</li>
            <li>Stripe reports billing events via webhooks.</li>
            <li>Resend reports email delivery status (delivered, opened, bounced) so we can maintain deliverability.</li>
          </ul>

          <h2 id="why">3. How we use information</h2>
          <ul className="list-disc list-outside pl-5 space-y-1">
            <li>Operate the marketplace: authenticate accounts, surface province-appropriate offers, route Shoppers to Merchant sites with cy_ref.</li>
            <li>Compute and settle cashback and Merchant performance fees.</li>
            <li>Fraud detection and abuse prevention (rate limiting, IP allowlisting, chargeback protection).</li>
            <li>Merchant billing (Stripe) and invoicing.</li>
            <li>Product improvement and analytics on aggregate, non-identifiable behaviour.</li>
            <li>Transactional and, if you opt in, marketing communications.</li>
            <li>Comply with legal obligations (tax, anti-fraud, court orders).</li>
          </ul>

          <h2 id="legal-basis">4. Legal basis (Canadian law)</h2>
          <p>
            Under PIPEDA we rely on your <strong>implied or express consent</strong>{" "}
            depending on the sensitivity of the information; and where consent
            is impractical, on limited PIPEDA exceptions such as fraud
            prevention, legal compliance and business transactions. Quebec
            residents may separately exercise Law 25 rights via the mechanisms
            in section 7.
          </p>

          <h2 id="share">5. When we share information</h2>
          <ul className="list-disc list-outside pl-5 space-y-1">
            <li><strong>Merchants</strong> — when you redeem an offer we share the cy_ref token, offer ID and, at the Merchant’s option, coarse conversion metadata (e.g., that a click occurred). Cyannect does not disclose your name or email to a Merchant without your consent (for example, when you opt into a Merchant’s post-purchase list).</li>
            <li><strong>Service providers (processors)</strong> — Stripe (billing), Resend (email), MongoDB Atlas (hosted database), Expo Application Services (mobile builds and push), Cloudflare (DNS/edge), and our cloud infrastructure providers. Each is contractually bound to process personal information only on our instructions.</li>
            <li><strong>Legal</strong> — where required by law, court order or lawful demand, or to protect Cyannect, users or the public.</li>
            <li><strong>Corporate transactions</strong> — in the event of a merger, acquisition or asset sale, information may be transferred subject to confidentiality obligations and a continuing right to opt out.</li>
          </ul>

          <h2 id="location">6. Location of processing</h2>
          <p>
            Personal information may be processed and stored in Canada, the
            United States and other jurisdictions where our service providers
            operate. When information is transferred outside Canada, we use
            appropriate contractual and technical safeguards.
          </p>

          <h2 id="rights">7. Your rights and choices</h2>
          <p>You may:</p>
          <ul className="list-disc list-outside pl-5 space-y-1">
            <li>Access, correct or update your account information from your settings screen.</li>
            <li>Request a copy of the personal information we hold about you.</li>
            <li>Request deletion of your account and associated personal information, subject to retention obligations (e.g., financial records).</li>
            <li>Withdraw consent to marketing emails at any time by clicking “unsubscribe”.</li>
            <li>Complain to the Office of the Privacy Commissioner of Canada, or (Quebec) the Commission d’accès à l’information.</li>
          </ul>
          <p>
            To exercise these rights, contact us at{" "}
            <a href="mailto:privacy@cyannect.com" className="underline text-cyan-700">privacy@cyannect.com</a>.
            We will respond within 30 days.
          </p>

          <h2 id="retention">8. Retention</h2>
          <p>
            We retain personal information for as long as it is needed for the
            purposes described in this Policy, or as required by law. Typical
            retention periods:
          </p>
          <ul className="list-disc list-outside pl-5 space-y-1">
            <li>Account records — for the life of the account plus 7 years after closure (financial records retention).</li>
            <li>Conversion/cashback ledger — 7 years (accounting).</li>
            <li>Session logs and analytics — up to 24 months.</li>
            <li>Marketing preferences — until you opt out plus 30 days.</li>
          </ul>

          <h2 id="security">9. Security</h2>
          <p>
            We use industry-standard safeguards, including HTTPS across all
            surfaces, bcrypt password hashing, SHA-256 hashed reset tokens,
            IP allowlisting during private beta, rate limiting, encrypted
            secrets in environment variables, and least-privilege database
            access. No system is perfectly secure — please use a strong,
            unique password and enable device-level protections.
          </p>

          <h2 id="children">10. Children</h2>
          <p>
            The Services are not directed to children under the age of
            majority in their province of residence. We do not knowingly
            collect personal information from minors. If you believe a minor
            has provided us with personal information, please contact us and
            we will delete it.
          </p>

          <h2 id="cookies">11. Cookies</h2>
          <p>
            We use only cookies that are strictly necessary for the operation
            of the Services (authentication, CSRF protection, attribution
            token pass-through). We do not use third-party advertising cookies.
            You can control cookies through your browser settings; disabling
            essential cookies will make the Services unusable.
          </p>

          <h2 id="changes">12. Changes to this Policy</h2>
          <p>
            We may update this Policy from time to time. Material updates
            will be highlighted at the top of the page and, where required,
            notified to you before they take effect.
          </p>

          <h2 id="contact">13. Contact</h2>
          <p>
            Cyannect Privacy Officer<br />
            Email:{" "}
            <a href="mailto:privacy@cyannect.com" className="underline text-cyan-700">privacy@cyannect.com</a>
            <br />
            Cyannect Inc., Ontario, Canada.
          </p>
        </div>
      </div>
    </div>
  );
}
